import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ProfileCompletion = () => {
  const { userProfile, currentUser, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    title: '',
    company: '',
    city: '',
    state: '',
    workPhone: '',
    cellPhone: '',
    bio: '',
    linkedIn: '',
    companyCategory: '',
    companyDescription: ''
  });

  // Legal agreement checkboxes
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);

  // Initialize with existing profile data
  useEffect(() => {
    if (userProfile) {
      setProfileData({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        title: userProfile.jobTitle || userProfile.title || '',
        company: userProfile.company || '',
        city: userProfile.city || '',
        state: userProfile.state || '',
        workPhone: userProfile.phone || userProfile.workPhone || '',
        cellPhone: userProfile.cellPhone || '',
        bio: userProfile.aboutMe || userProfile.bio || '',
        linkedIn: userProfile.linkedinUrl || userProfile.linkedIn || '',
        companyCategory: userProfile.companyCategory || '',
        companyDescription: userProfile.companyDescription || ''
      });
    }
  }, [userProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const requiredFields = ['firstName', 'lastName', 'title', 'company', 'city', 'state'];
    const missingFields = requiredFields.filter(field => !profileData[field]?.trim());

    if (missingFields.length > 0) {
      alert(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      return;
    }

    // Validate legal agreements
    if (!agreedToTerms) {
      alert('Please agree to the Terms and Conditions to continue');
      return;
    }

    if (!agreedToPrivacy) {
      alert('Please agree to the Privacy Policy to continue');
      return;
    }

    setLoading(true);

    try {
      console.log('Updating profile for user:', currentUser.uid);
      console.log('Profile data:', profileData);

      await updateUserProfile(currentUser.uid, {
        ...profileData,
        // Canonical field aliases — the Profile editor + member directory read
        // jobTitle/aboutMe/linkedinUrl/phone, NOT title/bio/linkedIn/workPhone.
        // Write both so onboarding data is actually visible afterward (was lost).
        jobTitle: profileData.title,
        aboutMe: profileData.bio,
        linkedinUrl: profileData.linkedIn,
        phone: profileData.workPhone,
        profileCompleted: true,
        profileCompletedAt: new Date(),
        agreedToTerms: true,
        agreedToPrivacy: true,
        legalAgreementDate: new Date()
      });

      console.log('Profile updated successfully');
      // Redirect to member dashboard/home
      navigate('/events', { replace: true });
    } catch (error) {
      console.error('Error updating profile:', error);
      console.error('Error details:', error.message);
      console.error('Error code:', error.code);
      alert(`Error updating profile: ${error.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name *
            </label>
            <input
              type="text"
              name="firstName"
              value={profileData.firstName}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name *
            </label>
            <input
              type="text"
              name="lastName"
              value={profileData.lastName}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Title *
            </label>
            <input
              type="text"
              name="title"
              value={profileData.title}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company *
            </label>
            <input
              type="text"
              name="company"
              value={profileData.company}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact & Location</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City *
            </label>
            <input
              type="text"
              name="city"
              value={profileData.city}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State *
            </label>
            <input
              type="text"
              name="state"
              value={profileData.state}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Work Phone
            </label>
            <input
              type="tel"
              name="workPhone"
              value={profileData.workPhone}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cell Phone
            </label>
            <input
              type="tel"
              name="cellPhone"
              value={profileData.cellPhone}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Details</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              LinkedIn Profile
            </label>
            <input
              type="url"
              name="linkedIn"
              value={profileData.linkedIn}
              onChange={handleInputChange}
              placeholder="https://linkedin.com/in/yourprofile"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Professional Bio
            </label>
            <textarea
              name="bio"
              value={profileData.bio}
              onChange={handleInputChange}
              rows="4"
              placeholder="Tell other members about your professional background, expertise, and interests..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Legal Agreements Section */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <h4 className="text-lg font-semibold text-gray-800 mb-4">Legal Agreements</h4>
        <div className="space-y-3">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              required
            />
            <span className="text-sm text-gray-700">
              I agree to the{' '}
              <Link
                to="/legal/terms-and-conditions"
                target="_blank"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Terms and Conditions
              </Link>
              {' '}*
            </span>
          </label>

          <label className="flex items-start">
            <input
              type="checkbox"
              checked={agreedToPrivacy}
              onChange={(e) => setAgreedToPrivacy(e.target.checked)}
              className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              required
            />
            <span className="text-sm text-gray-700">
              I agree to the{' '}
              <Link
                to="/legal/privacy-policy"
                target="_blank"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Privacy Policy
              </Link>
              {' '}*
            </span>
          </label>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          * Required to complete profile and access member features
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Complete Your Profile
            </h1>
            <p className="text-gray-600">
              Help other members get to know you by completing your professional profile
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Step {currentStep} of {totalSteps}
              </span>
              <span className="text-sm text-gray-500">
                {Math.round((currentStep / totalSteps) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentStep === 1}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading || !agreedToTerms || !agreedToPrivacy}
                  className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Saving...' : 'Complete Profile'}
                </button>
              )}
            </div>

            {/* Legal Agreement Warning */}
            {currentStep === 3 && (!agreedToTerms || !agreedToPrivacy) && (
              <div className="text-center mt-4">
                <p className="text-sm text-red-600">
                  Please agree to both the Terms and Conditions and Privacy Policy to complete your profile
                </p>
              </div>
            )}
          </form>

          {/* Skip Option */}
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/events')}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              Skip for now - I'll complete this later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletion;
